import { MapPin, Clock, Phone, Mail, Star } from "lucide-react";
import Seo, { localBusinessSchema } from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { SITE } from "@/data/site";

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact & Visit — Let Go Wellness Clinic in Waterbury, CT"
        description="Visit Let Go Wellness at 808 Highland Ave, Waterbury, CT 06708. Hours, phone, directions and map. Therapy in person or by telehealth across Connecticut."
        path="/contact"
        jsonLd={localBusinessSchema}
      />
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <Reveal>
            <div className="overline mb-6">Contact & visit</div>
            <h1 className="max-w-3xl font-serif text-5xl leading-tight text-ink sm:text-6xl">Waterbury's <span className="italic text-clay">quietest</span> waiting room.</h1>
            <p className="mt-6 max-w-xl font-sans text-base leading-relaxed text-ink-soft">A peaceful Highland Avenue setting with morning light, linen sofas and tea always on. Easy on-site parking, with stroller and wheelchair access.</p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto grid max-w-6xl gap-px overflow-hidden border-x border-border bg-border md:grid-cols-2">
          <div className="bg-white p-10 sm:p-14">
            <div className="space-y-8" data-testid="contact-nap">
              <div className="flex gap-4"><MapPin className="mt-1 shrink-0 text-clay" size={20} /><div><div className="overline mb-1">Address</div><p className="font-sans text-base text-ink-soft">{SITE.street}<br />{SITE.city}, {SITE.state} {SITE.zip}</p></div></div>
              <div className="flex gap-4"><Clock className="mt-1 shrink-0 text-clay" size={20} /><div><div className="overline mb-1">Hours</div>{SITE.hours.map((h) => <p key={h.d} className="font-sans text-base text-ink-soft">{h.d} · {h.h}</p>)}</div></div>
              <div className="flex gap-4"><Phone className="mt-1 shrink-0 text-clay" size={20} /><div><div className="overline mb-1">Phone</div><p className="font-sans text-base text-ink-soft">Office <a href={`tel:${SITE.phoneOffice}`} className="hover:text-ink">{SITE.phoneOffice}</a><br />Cell <a href={`tel:${SITE.phoneCell}`} className="hover:text-ink">{SITE.phoneCell}</a></p></div></div>
              <div className="flex gap-4"><Mail className="mt-1 shrink-0 text-clay" size={20} /><div><div className="overline mb-1">Email</div><a href={`mailto:${SITE.email}`} className="font-sans text-base text-ink-soft hover:text-ink">{SITE.email}</a></div></div>
            </div>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <a href={SITE.mapLink} target="_blank" rel="noreferrer" data-testid="contact-directions" className="rounded-full border border-border px-6 py-3 text-center font-sans text-sm text-ink-soft transition-colors hover:bg-linen">Get directions</a>
              <a href="/new-patient" data-testid="contact-book" className="rounded-full bg-ink px-6 py-3 text-center font-sans text-sm text-linen transition-colors hover:bg-ink/85">Book a first session</a>
            </div>
          </div>
          <div className="min-h-[420px] bg-white">
            <iframe title="Map to Let Go Wellness, 808 Highland Ave, Waterbury, CT" src={SITE.mapEmbed} className="h-full min-h-[420px] w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="contact-map" />
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-linen py-16" data-testid="review-cta">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="mb-3 flex justify-center gap-1 text-clay">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={18} fill="currentColor" />)}
          </div>
          <p className="font-serif text-2xl italic text-ink">Has therapy here helped you?</p>
          <p className="mx-auto mt-3 max-w-md font-sans text-base text-ink-soft">A few kind words on Google help other people in Waterbury find the support they need. It only takes a minute.</p>
          <a href={SITE.googleReviewUrl} target="_blank" rel="noreferrer" data-testid="review-link" className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 font-sans text-sm text-linen transition-colors hover:bg-ink/85">
            <Star size={15} /> Leave a Google review
          </a>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="font-serif text-2xl italic text-ink">In crisis right now? You're not alone.</p>
          <p className="mt-3 font-sans text-base text-ink-soft">Call or text <a href="tel:988" className="text-clay underline-offset-4 hover:underline">988</a> — the Suicide & Crisis Lifeline. Free, confidential, 24/7.</p>
        </div>
      </section>
    </>
  );
}
